<template>
  <div ref="paymentRequestFormRef" class="tw-max-w-[916px] tw-mx-auto">
    <PageHeader>
      <template #header-breadcrumbs>
        <WdsButtonGhost secondary @click="cancelPaymentRequest">
          <WdsStack gap-sm>
            <WdsIcon :size="16" :name="WdsIconNameEnum.UNDO" />
            {{ store.isEditable ? $t('Return to Payment Requests') : $t('Back') }}
          </WdsStack>
        </WdsButtonGhost>
      </template>
      <template #header-title>
        {{ $t('Payment request') }}
        <span v-if="billId">#{{ billId }}</span>
      </template>
      <template #header-subheading>
        <div class="tw-truncate tw-max-w-full" :title="`${store.companyName} / ${store.jobName}`">
          {{ store.companyName }} / {{ store.jobName }}
        </div>
      </template>
      <template #header-metadata>
        <WdsTag v-if="!persistedBill || store.isDraft" sm warning>
          {{ !persistedBill ? $t('Unsaved') : $t('Draft') }}
        </WdsTag>
        <WdsTag v-else-if="isApproved" sm warning>
          {{ $t('Approved') }}
        </WdsTag>
      </template>
    </PageHeader>
    <template v-if="$slots['blocking-alerts'] && store.isEditable">
      <slot name="blocking-alerts" />
    </template>
    <template v-else>
      <template v-if="$slots['non-blocking-alerts']">
        <div class="tw-my-8.5">
          <slot name="non-blocking-alerts" />
        </div>
      </template>
      <Skeleton v-if="store.isFetchingData" />
      <div v-show="!store.isFetchingData">
        <BillCreateComplianceNotices v-if="store.isEditable" :hire-id="hireId" class="tw-my-8.5" />
        <WdsStack direction-column class="payment-request-form-container">
          <WdsStack gap-md direction-column data-test="payment-request-billing-period-section">
            <WdsHeading type="subtitle">{{ $t('Billing period') }}</WdsHeading>
            <WdsStack class="tw-p-4 tw-bg-true-gray-50" direction-column gap-lg>
              <BillingPeriodInputs />
            </WdsStack>
            <BillingPeriodBreakdown />
          </WdsStack>
          <WdsStack gap-md direction-column>
            <WdsHeading type="subtitle">{{ $t('Charge for your work') }}</WdsHeading>
            <PaymentRequestTypeControl v-if="allowTimeXRatePaymentRequests && store.isEditable" />
            <RateDisplay />
            <WdsStack class="tw-p-4 tw-bg-true-gray-50" direction-column gap-lg>
              <MilestoneSelection
                v-if="store.paymentRequestType === PaymentRequestTypeEnum.MILESTONE"
              />
              <template v-else>
                <TimesheetSelection />
                <AmountInputs :rate-units="rateUnits" />
              </template>
              <SubtotalBreakdown />
            </WdsStack>
          </WdsStack>
          <Expenses v-if="store.paymentRequestType !== PaymentRequestTypeEnum.MILESTONE" />
          <WdsStack gap-md direction-column>
            <WdsInputArea
              v-model="store.paymentRequestForm.comments"
              data-test="payment-request-comments-input"
              :label="store.isEditable ? $t('Comments (optional)') : $t('Comments')"
              :placeholder="$t('Add additional information...')"
              :disabled="!store.isEditable"
            ></WdsInputArea>
          </WdsStack>
          <Overview />
          <WdsAlert warning icon :hidden="!validationStore?.v$?.$errors?.length">
            {{
              validationStore?.v$?.expenseReports?.$errors?.[0]?.$message ||
              $t("You're missing some information")
            }}
          </WdsAlert>
        </WdsStack>
      </div>
    </template>
    <template v-if="!$slots['blocking-alerts'] && !store.isFetchingData && store.isEditable">
      <ActionBar
        :width="`${paymentRequestFormRef?.offsetWidth}px`"
        style="
          box-shadow:
            0 1px 3px 0 rgb(0 0 0 / 0.1),
            0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
        "
      >
        <WdsStack justify-space-between align-center class="tw-w-full">
          <WdsSpinner v-if="store.isSubmitting" indeterminate />
          <WdsText
            v-else-if="!persistedBill || store.isDraft || (persistedBill && store.isEditable)"
            small
          >
            {{ !persistedBill ? $t('Unsaved') : $t('Last saved') }}
            <template #subText>
              {{ !persistedBill ? $t('Send or save as draft to keep changes') : updatedAt }}
            </template>
          </WdsText>
          <span v-else></span>
          <Submit ref="submitFormRef" />
        </WdsStack>
      </ActionBar>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  WdsAlert,
  WdsButtonGhost,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInputArea,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import { computed, onMounted, ref, watchEffect } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import Expenses from '@/components/PaymentRequest/Expenses/Expenses.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { RateType } from '@/enums'
import BillCreateComplianceNotices from '@/components/compliances/hooks/BillCreateComplianceNotices.vue'
import { TimesheetTypeEnum, PaymentRequestTypeEnum } from '@/components/PaymentRequest/enums'
import Submit from '@/components/PaymentRequest/Submit.vue'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useValidationStore } from '@/components/PaymentRequest/store/useValidationStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'
import { useTaxStore } from '@/components/PaymentRequest/store/useTaxStore'
import { useTranslation } from '@/composables/translation'
import ActionBar from '@/components/ActionBar.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import PaymentRequestTypeControl from '@/components/PaymentRequest/PaymentRequestTypeControl.vue'
import AmountInputs from '@/components/PaymentRequest/AmountInputs.vue'
import SubtotalBreakdown from '@/components/PaymentRequest/SubtotalBreakdown.vue'
import TimesheetSelection from '@/components/PaymentRequest/TimesheetSelection.vue'
import BillingPeriodBreakdown from '@/components/PaymentRequest/BillingPeriodBreakdown.vue'
import BillingPeriodInputs from '@/components/PaymentRequest/BillingPeriodInputs.vue'
import RateDisplay from '@/components/PaymentRequest/RateDisplay.vue'
import Overview from '@/components/PaymentRequest/Overview.vue'
import Skeleton from '@/components/PaymentRequest/Skeleton.vue'
import { useHtmlFormStore } from '@/components/PaymentRequest/store/useHtmlFormStore'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import PageHeader from '@/components/PageHeader.vue'
import MilestoneSelection from '@/components/PaymentRequest/MilestoneSelection.vue'
import { useTimesheetParsingStore } from '@/components/Timesheet/store/useTimesheetParsingStore'
import { useAiConsentStore } from '@/state/useAiConsentStore'

interface Props {
  hireId: number
  hireGlobalId: string
  workerGlobalId: string
  billId?: number
  enableExpenseReports?: boolean
  requireTimesheets?: boolean
  vatTerminology: string
  vatRate: number
  enableWorksomeTimesheetFeature?: boolean
  enableWorksomeTimesheetLinesFeature?: boolean
  enableWorksomeIntelligence?: boolean
  requiresStartAndEndTimesOnTimesheets?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  enableExpenseReports: false,
  requireTimesheets: false,
  billId: 0,
  enableWorksomeTimesheetFeature: false,
  enableWorksomeTimesheetLinesFeature: false,
  enableWorksomeIntelligence: false,
  requiresStartAndEndTimesOnTimesheets: false,
})
const customFieldValues = ref([])

const emit = defineEmits(['cancel'])

const { $t } = useTranslation()
const store = usePaymentRequestStore()
const timesheetsStore = useTimesheetStore()
const timesheetFormStore = useTimesheetFormStore()
const validationStore = useValidationStore()
const expensesStore = useExpensesStore()
const taxStore = useTaxStore()
const htmlFormStore = useHtmlFormStore()
const timesheetParsingStore = useTimesheetParsingStore()
const aiConsentStore = useAiConsentStore()

const paymentRequestFormRef = ref<HTMLElement | null>(null)

// Update the stores using the prop values
onMounted(async () => {
  aiConsentStore.hasConsent = !!currentUser?.ai_consent
  store.hireGlobalId = props.hireGlobalId
  store.hireLocalId = props.hireId
  store.workerId = props.workerGlobalId
  timesheetsStore.requireTimesheets = props.requireTimesheets
  store.vatRate = props.vatRate
  store.enableWorksomeIntelligence = props.enableWorksomeIntelligence
  store.vatTerminology = props.vatTerminology
  expensesStore.enableExpenseReports = props.enableExpenseReports
  store.billId = props.billId || 0
  store.requiresStartAndEndTimesOnTimesheets = props.requiresStartAndEndTimesOnTimesheets
})

const rateUnitsTerminology = {
  [RateType.HOURLY]: $t('hours'),
  [RateType.DAILY]: $t('days'),
  [RateType.WEEKLY]: $t('weeks'),
  [RateType.MONTHLY]: $t('months'),
  [RateType.FIXED]: $t('fixed'),
}
const rateUnits = computed(() => rateUnitsTerminology[store.paymentRequestRate.rateType])

const { onResult, loading } = useQuery(
  gql`
    query HireAndBill($hireLocalId: Int!, $billLocalId: Int!) {
      hire(localId: $hireLocalId) {
        localId
        threadId
        rate
        ratetype
        ratetypeName
        ratetypeNameSlash
        currency
        freelancerFee
        market
        hasFreelancerVat
        hasPayroll
        yjob {
          name
          localId
        }
        company {
          globalId
          name
        }
        milestones(first: 100) {
          data {
            id
            name
            purchaseOrderNumber
            status
            details {
              id
              dueDate
              amount
            }
          }
        }
      }
      me {
        globalId
        activeAccount {
          globalId
          ... on HasBills {
            bill(localId: $billLocalId) {
              globalId
              localId
              isDraft
              isIssued
              isApproved
              isRejected
              isCancelled
              updatedAt
              dateStart
              dateEnd
              message #comment
              rate # used in case of fixed rate
              rateType # pull from hire, not here
              rateTypeName
              ratetypeNameSlash
              hours
              days
              weeks
              currency
              hasWorksomeTimesheet
              worksomeTimesheet {
                id
              }
              timeSheet
              timeSheetFormat
              tax_lines {
                name
                type
                value
              }
              files(first: 10, page: 1) {
                data {
                  globalId
                  localId
                  name
                  storageName
                  icon {
                    icon
                    color
                    thumbnail
                  }
                }
              }
              expenseReports(first: 100, page: 1) {
                data {
                  globalId
                  localId
                  title
                  description
                  total
                  expenses {
                    globalId
                    localId
                    vendor
                    description
                    amount
                    currency
                    expenseAt
                    files {
                      globalId
                      localId
                      name
                      storageName
                      icon {
                        icon
                        color
                        thumbnail
                      }
                    }
                  }
                }
              }
              milestones(first: 100) {
                data {
                  id
                  name
                  purchaseOrderNumber
                  status
                  details {
                    id
                    dueDate
                    amount
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
  () => ({
    billLocalId: store.billId,
    hireLocalId: props.hireId,
  }),
  () => ({
    // clientId: 'publicApi',
    fetchPolicy: 'no-cache',
  }),
)

const persistedBill = ref(null)
const isApproved = ref(false)
const updatedAt = ref(null)
const hireRateTypeIsFixed = ref(false)
const persistedBillRateTypeIsFixed = ref(false)

onResult((result) => {
  const hire = result?.data?.hire ?? {}
  const bill = result?.data?.me?.activeAccount?.bill ?? {}
  if (!hire.localId) return
  store.hydratePaymentRequestStoreFromHire(hire)
  hireRateTypeIsFixed.value = hire.ratetype === RateType.FIXED
  if (!bill.localId) return
  timesheetsStore.worksomeTimesheetId = bill.worksomeTimesheet?.id
  store.hydratePaymentRequestStoreFromBill(bill)
  persistedBill.value = bill
  updatedAt.value = bill.updatedAt
  isApproved.value = bill.isApproved
  persistedBillRateTypeIsFixed.value =
    bill.rateType === RateType.FIXED || bill.rateType === RateType.MONTHLY

  // Support old bills which were allowed a different rate and/or rate type than
  // the hire contract
  if (
    bill.rateType !== RateType.MONTHLY &&
    (bill.rate !== hire.rate || bill.rateType !== hire.ratetype)
  ) {
    store.paymentRequestRate = {
      rate: bill.rate,
      rateType: bill.rateType,
      rateTypeName: bill.rateTypeName,
      rateTypeNameSlash: bill.ratetypeNameSlash,
    }
  }
  expensesStore.updateExpenseReports(
    bill.expenseReports?.data?.map((expenseReport) => ({
      globalId: expenseReport.globalId,
      localId: expenseReport.localId,
      title: expenseReport.title,
      description: expenseReport.description,
      total: expenseReport.total,
      expenses: expenseReport.expenses,
    })) || [],
  )
  timesheetsStore.hasWorksomeTimesheet = bill.hasWorksomeTimesheet
  if (bill.timeSheetFormat === TimesheetTypeEnum.SHEET) {
    timesheetsStore.timesheetSpreadsheetValue = bill.timeSheet
  }
  if (bill.timeSheetFormat === TimesheetTypeEnum.TEXT) {
    timesheetsStore.timesheetComment = JSON.parse(bill.timeSheet)
  }
  if (bill.hasWorksomeTimesheet) {
    timesheetParsingStore.uploadedFiles = bill.files?.data || []
  } else if (bill.timeSheetFormat === TimesheetTypeEnum.FILE) {
    timesheetsStore.timesheetFiles = bill.files?.data
  }
  bill.tax_lines?.forEach((taxLine) =>
    taxStore.patchTaxLine({
      rate: taxLine.value / 100,
      terminology: taxLine.name,
    }),
  )
})

const cancelPaymentRequest = () => {
  store.clearPaymentRequest()
  expensesStore.clearExpenses()
  timesheetsStore.clearTimesheets()
  validationStore.v$.$reset()
  emit('cancel')
  if (!store.isEditable) {
    history.back()
  } else {
    window.location.href = laravelNamedRoute(namedRoutes.billing.bills)
  }
}

/**
 * We must support bills which were made in a more flexible system
 * which allowed for fixed rate hires to receive bills with 'Time X Rate'
 * and also allowed a rate which is different from the rate on the hire
 */
const allowTimeXRatePaymentRequests = computed(() => {
  // Skip TimeXRatePaymentRequests when type of milestone
  if (store.paymentRequestType === PaymentRequestTypeEnum.MILESTONE) {
    return false
  }

  // When hire is fixed, and either no bill, or bill is also fixed
  if (hireRateTypeIsFixed.value && (!persistedBill.value || persistedBillRateTypeIsFixed.value)) {
    return false
  }

  // If hire is TimeXRate, we can bill both types regardless of
  // If persisted bill doesn't exist, is fixed, or is TimeXRate
  return true
})

// Wait for the possible attached timesheet to load
watchEffect(() => {
  store.isFetchingData =
    loading.value ||
    (timesheetsStore.worksomeTimesheetId && timesheetFormStore.isLoadingTimesheet) ||
    false
})
watchEffect(() => {
  store.enableWorksomeTimesheetFeature = props.enableWorksomeTimesheetFeature
  store.enableWorksomeTimesheetLinesFeature = props.enableWorksomeTimesheetLinesFeature
})
</script>

<style scoped lang="scss">
@tailwind base;

:deep(.wds-text) {
  .wds-text-primary {
    @apply tw-text-gray-850;
  }
  .wds-text-secondary .tw-text-base {
    @apply tw-text-gray-600;
  }
}

.payment-request-form-container {
  @apply tw-my-8.5;
  @apply tw-gap-8.5;
}
</style>
